import React, { useEffect, useRef } from 'react';

import { VideoPlayerCoreProps, GenericVideoPlayerInterface, trackVideoEngagement } from '../videoUtils';
import { loadYouTubeAPI, createPlaybackRateChangeHandler } from './youtubeUtils';
import { IS_DISCORD_ACTIVITY } from '@/globals';

function createYouTubePlayerWrapper(player: YT.Player): GenericVideoPlayerInterface {
    return {
        getCurrentTime: () => {
            return typeof player?.getCurrentTime === 'function' ? player.getCurrentTime() : undefined;
        },
        getDuration: () => {
            return typeof player?.getDuration === 'function' ? player.getDuration() : undefined;
        },
        isEnded: () => {
            return typeof player?.getPlayerState === 'function'
                ? player.getPlayerState() === window.YT?.PlayerState.ENDED
                : undefined;
        },
    };
}

export function YouTubePlayerCore({
    videoUrl,
    videoId,
    className,
    hasControls,
    shouldAutoplay,
    playerRef,
    onPlay,
    onPause,
    onEnded,
    onReady,
}: VideoPlayerCoreProps) {
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const [isYouTubeAPIReady, setIsYouTubeAPIReady] = React.useState(false);
    const nativePlayerRef = useRef<YT.Player | null>(null);

    useEffect(() => {
        loadYouTubeAPI(() => setIsYouTubeAPIReady(true));
    }, []);

    const onPlaybackRateChange = createPlaybackRateChangeHandler(videoUrl);

    useEffect(() => {
        if (!isYouTubeAPIReady) return;
        if (!videoUrl) return;
        if (!videoUrl.includes('youtube.com/watch')) return;
        if (!videoId) {
            console.warn('Invalid YouTube video URL');
            return;
        }

        if (playerContainerRef.current && window.YT && window.YT.Player) {
            if (nativePlayerRef.current) {
                nativePlayerRef.current.destroy();
            }

            const usePrivacyEnhancedMode =
                !IS_DISCORD_ACTIVITY && process.env.PUBLIC_USE_YOUTUBE_PRIVACY_MODE === 'true';

            const player = new window.YT.Player(playerContainerRef.current, {
                ...(usePrivacyEnhancedMode && { host: 'https://www.youtube-nocookie.com' }),
                videoId: videoId,
                width: '100%',
                height: '100%',
                playerVars: {
                    playsinline: 1,
                    enablejsapi: 1,
                    controls: hasControls ? 1 : 0,
                    disablekb: hasControls ? 0 : 1,
                    autoplay: shouldAutoplay ? 1 : 0,
                },
                events: {
                    onReady: (event) => {
                        const iframe = event.target.getIframe();
                        if (iframe) {
                            iframe.setAttribute('title', 'YouTube video player');
                            iframe.setAttribute(
                                'allow',
                                'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
                            );
                            iframe.setAttribute('referrerPolicy', 'strict-origin-when-cross-origin');
                            iframe.setAttribute('allowFullScreen', '');
                        }

                        onReady();
                    },
                    onStateChange: (event: any) => {
                        const player = event.target;
                        const state = event.data;
                        const currentTime = player.getCurrentTime();
                        const duration = player.getDuration();

                        // Track video engagement events
                        switch (state) {
                            case window.YT.PlayerState.PLAYING:
                                trackVideoEngagement('play', videoUrl, [], [currentTime, duration]);
                                onPlay();
                                break;

                            case window.YT.PlayerState.PAUSED:
                                trackVideoEngagement('pause', videoUrl, [], [currentTime, duration]);
                                onPause();
                                break;

                            case window.YT.PlayerState.ENDED:
                                trackVideoEngagement('ended', videoUrl, [], [duration]);
                                onEnded();
                                break;

                            case window.YT.PlayerState.BUFFERING:
                                trackVideoEngagement('buffering', videoUrl, [], [currentTime, duration]);
                                break;

                            default:
                                break;
                        }
                    },
                    onPlaybackRateChange: onPlaybackRateChange,
                },
            });
            nativePlayerRef.current = player;
            playerRef.current = createYouTubePlayerWrapper(player);
        }

        return () => {
            if (nativePlayerRef.current) {
                nativePlayerRef.current.destroy();
                nativePlayerRef.current = null;
            }
            playerRef.current = null;
        };
    }, [isYouTubeAPIReady, videoUrl, hasControls, videoId]);

    if (!videoUrl?.includes('youtube.com/watch')) {
        console.warn('Invalid YouTube video URL');
        return null;
    }

    if (!videoId) {
        console.warn('Invalid YouTube video URL');
        return null;
    }

    return <div ref={playerContainerRef} className={className}></div>;
}
